import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PersonButton } from '@/features/people/PersonDrawer';
import { describePositionsChange, positionsPatch, POSITIONS_HISTORY_ACTIONS, type Employee, type EmployeeHistoryEntry } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useUi } from '@/lib/store';
import { fmt } from '@/lib/format';
import { useWorkspaceId } from '@/lib/hooks';
import { useState } from 'react';
import { ConfirmDialog, Modal, Spinner } from '@/components/ui';
import { useWfLookups } from './common';
import { PositionPicker } from './PositionPicker';

// t331u: shared with the phone pages.
export { describePositionsChange } from '@tradeoutlet/shared';

export function HistoryList({ entries, describe }: { entries: EmployeeHistoryEntry[]; describe: (e: EmployeeHistoryEntry) => string }) {
  if (!entries.length) return <p className="text-sm text-slate-500">No changes recorded yet.</p>;
  return (
    <ol className="space-y-2 text-sm" data-testid="history">
      {entries.map(e => (
        <li key={e.id} className="flex gap-3">
          <time className="w-36 shrink-0 text-xs text-slate-500" dateTime={e.created_at}>{fmt.dateTime(e.created_at)}</time>
          <span>{e.actor_id ? <PersonButton userId={e.actor_id} name={e.actor_name} className="rounded font-medium hover:underline">{e.actor_name}</PersonButton> : <span className="font-medium">{e.actor_name ?? 'System'}</span>} · {describe(e)}</span>
        </li>
      ))}
    </ol>
  );
}

/** Feedback #4: positions with a primary, explicit Save/Cancel, and the change history. */
export function PositionsTab({ emp, onSaved }: { emp: Employee; onSaved?: (e: Employee) => void }) {
  const ws = useWorkspaceId();
  const qc = useQueryClient();
  const toast = useUi(s => s.toast);
  const { positions } = useWfLookups();
  const hist = useQuery({ queryKey: ['wf', ws, 'employee-history', emp.user_id, 'positions'], queryFn: () => api.wf.employeeHistory(ws!, emp.user_id, POSITIONS_HISTORY_ACTIONS), enabled: Boolean(ws) });
  return (
    <div className="space-y-6">
      <section>
        <h3 className="mb-2 text-sm font-semibold">Positions</h3>
        <p className="mb-3 text-xs text-slate-500">Positions decide which shifts {emp.display_name} can be given. The starred one is their primary position, used first on the rota. Access is set by Roles.</p>
        <PositionPicker positions={positions} personName={emp.display_name} value={{ ids: emp.position_ids, primary: emp.primary_position_id ?? null }}
          onSave={async v => {
            try {
              const r = await api.wf.updateEmployee(ws!, emp.user_id, positionsPatch(v));
              toast('Positions saved', 'success');
              onSaved?.(r);
              void qc.invalidateQueries({ queryKey: ['wf', ws] });
            } catch (e) { toast(errorMessage(e), 'error'); }
          }} />
      </section>
      <section>
        <h3 className="mb-2 text-sm font-semibold">History</h3>
        {hist.isLoading ? <Spinner /> : <HistoryList entries={hist.data?.entries ?? []} describe={e => describePositionsChange(e.metadata)} />}
      </section>
    </div>
  );
}

/** Per-person result of a bulk positions action (pure, for tests). null = nothing to change. */
export function bulkPositionsPatch(e: Pick<Employee, 'position_ids' | 'primary_position_id'>, ids: string[], action: 'add' | 'remove') {
  const next = action === 'add' ? [...new Set([...e.position_ids, ...ids])] : e.position_ids.filter(x => !ids.includes(x));
  if (next.length === e.position_ids.length && next.every(x => e.position_ids.includes(x))) return null;
  const primary = e.primary_position_id && next.includes(e.primary_position_id) ? e.primary_position_id : next[0] ?? null;
  return { position_ids: next, primary_position_id: primary };
}

/** Feedback #4: the Users bulk action - same picker, add or remove for everyone selected, confirmed. */
export function BulkPositionsModal({ count, onApply, onClose }: { count: number; onApply: (ids: string[], action: 'add' | 'remove') => Promise<void>; onClose: () => void }) {
  const { positions } = useWfLookups();
  const [action, setAction] = useState<'add' | 'remove'>('add');
  const [pending, setPending] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const names = (ids: string[]) => ids.map(id => positions.find(p => p.id === id)?.name).filter(Boolean).join(', ');
  return (
    <Modal open onClose={onClose} title={`Positions for ${count} ${count === 1 ? 'person' : 'people'}`}>
      <div className="space-y-4">
        <fieldset className="flex gap-4 text-sm">
          <legend className="sr-only">Action</legend>
          <label className="flex items-center gap-1.5"><input type="radio" name="bulk-pos" checked={action === 'add'} onChange={() => setAction('add')} />Add positions</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="bulk-pos" checked={action === 'remove'} onChange={() => setAction('remove')} />Remove positions</label>
        </fieldset>
        <PositionPicker mode="bulk" positions={positions} value={{ ids: [], primary: null }} saveLabel={action === 'add' ? 'Add…' : 'Remove…'} onCancel={onClose}
          onSave={v => { if (v.ids.length) setPending(v.ids); }} />
      </div>
      {pending && (
        <ConfirmDialog open danger={action === 'remove'} busy={busy} title={action === 'add' ? 'Add positions?' : 'Remove positions?'} confirmLabel={action === 'add' ? 'Add' : 'Remove'}
          onCancel={() => setPending(null)} onConfirm={async () => { setBusy(true); try { await onApply(pending, action); onClose(); } finally { setBusy(false); } }}>
          {action === 'add' ? 'Add' : 'Remove'} {names(pending)} {action === 'add' ? 'to' : 'from'} {count} {count === 1 ? 'person' : 'people'}. Each change is recorded in their history.
        </ConfirmDialog>
      )}
    </Modal>
  );
}
